import type { AiGeneratedImage } from 'mock/generated/types';
import { useMemo } from 'react';

import { getImageOrgId } from 'src/modules/homeownerApp/helpers';
import {
  useActiveOriginalImage,
  useHomeownerAppContext,
} from 'src/modules/homeownerApp/hooks';
import {
  CreatedFrom,
  MY_UPLOAD_STYLE,
} from 'src/modules/homeownerApp/static_values';
import type {
  AiOrSampleDisplayImage,
  AiOrSampleOriginalImage,
} from 'src/modules/homeownerApp/types';
import type { AiDisplayImage } from 'src/shared/graphql/generated';
import {
  useAiAvailableConfigurationsQuery,
  usePublicOrgInfoQuery,
} from 'src/shared/graphql/generated';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';

type FeaturedDisplayImage = AiDisplayImage & {
  style: string;
  createdFrom: CreatedFrom.STYLE | CreatedFrom.ORIGINAL;
  aiGeneratedImage: { thumbnailUrl: string };
};

interface UseEmbedDisplayImagesResponse {
  image?: AiOrSampleOriginalImage | null;
  embedDisplayImages?: FeaturedDisplayImage[];
  loading: boolean;
  supportedStylesAndOriginal: AiGeneratedImage['style'][];
}

const supportedCreatedFrom: string[] = [
  CreatedFrom.ORIGINAL,
  CreatedFrom.STYLE,
];

const sortedAndFilteredByStyle = (
  images: AiOrSampleDisplayImage[] | undefined | null,
  styles: string[],
) =>
  images
    ?.filter(
      (image): image is FeaturedDisplayImage =>
        !!image.style &&
        styles.includes(image.style) &&
        supportedCreatedFrom.includes(image.createdFrom),
    )
    .sort((a, b) => {
      const aIndex = styles.indexOf(a.style);
      const bIndex = styles.indexOf(b.style);
      return aIndex - bIndex;
    }) || [];

/**
 * If logged in, returns the display images for the given original image, sorted
 * by the order of the supported styles for the org of the image.
 * If anonymous, returns the featured display images for the given original image,
 * sorted by the order of the supported styles for the org of the embed.
 * Only returns images that are created from the original image or a style.
 */
export const useEmbedDisplayImages = (
  aiOriginalImageId: number,
): UseEmbedDisplayImagesResponse => {
  const { user, loggedIn } = useHomeownerAppContext();
  const { image, loading } = useActiveOriginalImage({
    originalImageId: aiOriginalImageId,
    variables: { featured: !loggedIn },
  });

  const { affOrgPublicId } = useGlobalStorageContext();

  const { data: publicOrgInfo, loading: publicOrgInfoLoading } =
    usePublicOrgInfoQuery({
      variables: { publicIdentifier: affOrgPublicId || '' },
      skip: !affOrgPublicId || loggedIn,
    });

  const imageOrgId = getImageOrgId(image, user);
  const publicOrgId = publicOrgInfo?.publicOrgInfo?.id;

  // If the user is logged in, we want to use the orgId of the image
  // If the user isn't logged in, we want to use the public org id
  // provided to the embed.
  const orgId = useMemo(() => {
    if (loggedIn) {
      return imageOrgId;
    }
    return publicOrgId;
  }, [imageOrgId, loggedIn, publicOrgId]);

  const { data: aiAvailableStylesData, loading: availableStylesLoading } =
    useAiAvailableConfigurationsQuery({
      variables: {
        orgId: orgId ? Number(orgId) : undefined,
        spaceType: image?.spaceType || 'exterior',
      },
      ssr: false,
    });
  const supportedStyles =
    aiAvailableStylesData?.aiAvailableConfigurations?.styles;

  // We want to include My upload, and have it as the first item in the list.
  // The other ordering doesn't matter, so we can just use whatever ordering the API gives us.
  const supportedStylesAndOriginal = useMemo(
    () => [MY_UPLOAD_STYLE, ...(supportedStyles || [])],
    [supportedStyles],
  );

  const embedDisplayImages = useMemo(
    () =>
      sortedAndFilteredByStyle(
        image?.aiDisplayImages,
        supportedStylesAndOriginal,
      ),
    [image?.aiDisplayImages, supportedStylesAndOriginal],
  ) satisfies FeaturedDisplayImage[];

  return {
    image,
    embedDisplayImages,
    loading:
      loading || publicOrgInfoLoading || availableStylesLoading || !image,
    supportedStylesAndOriginal,
  };
};
